import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store'
import { api, ApiError } from '../../../shared/lib/api'
import type { AuthUser } from '@rondela/shared'

// ===========================================
// Página de verificación de magic link
// Usa estado local para evitar interferencia con isLoading global
// ===========================================

export function VerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [isVerifying, setIsVerifying] = useState(true)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Ref para evitar doble ejecución en StrictMode
  const verificationAttempted = useRef(false)

  useEffect(() => {
    if (!token) {
      setIsVerifying(false)
      return
    }

    // Evitar doble ejecución en StrictMode
    if (verificationAttempted.current) {
      return
    }
    verificationAttempted.current = true

    const verify = async () => {
      try {
        setIsVerifying(true)
        setError(null)
        const response = await api.post<{ user: AuthUser }>('/api/auth/verify', { token })
        // Actualizar el store con el usuario y asegurar isLoading=false
        useAuthStore.setState({ user: response.user, isLoading: false })
        setVerified(true)
        setIsVerifying(false)
        // Redirigir después de un momento para mostrar el mensaje de bienvenida
        setTimeout(() => {
          navigate('/', { replace: true })
        }, 1500)
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Error al verificar el enlace'
        setError(message)
        setIsVerifying(false)
      }
    }

    verify()
  }, [token, navigate])

  // Sin token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div style={{ fontSize: '3rem' }}>⚠️</div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              marginTop: 'var(--spacing-md)',
            }}
          >
            Enlace inválido
          </h1>
          <p
            style={{
              color: 'var(--color-gray-600)',
              marginTop: 'var(--spacing-sm)',
            }}
          >
            No se encontró un token de verificación.
          </p>
          <Link to="/auth/login" className="btn btn-primary btn-full mt-4">
            Solicitar nuevo enlace
          </Link>
        </div>
      </div>
    )
  }

  // Verificando
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="flex justify-center mb-4">
            <div className="spinner" style={{ width: '2rem', height: '2rem' }} />
          </div>
          <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
            Verificando...
          </h1>
          <p
            style={{
              color: 'var(--color-gray-600)',
              marginTop: 'var(--spacing-sm)',
            }}
          >
            Estamos confirmando tu acceso
          </p>
        </div>
      </div>
    )
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div style={{ fontSize: '3rem' }}>❌</div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              marginTop: 'var(--spacing-md)',
            }}
          >
            Error de verificación
          </h1>
          <p
            style={{
              color: 'var(--color-gray-600)',
              marginTop: 'var(--spacing-sm)',
            }}
          >
            {error}
          </p>
          <Link to="/auth/login" className="btn btn-primary btn-full mt-4">
            Solicitar nuevo enlace
          </Link>
        </div>
      </div>
    )
  }

  // Verificado
  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card text-center" style={{ maxWidth: '400px', width: '100%' }}>
          <div style={{ fontSize: '3rem' }}>✅</div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              marginTop: 'var(--spacing-md)',
            }}
          >
            ¡Bienvenido!
          </h1>
          <p
            style={{
              color: 'var(--color-gray-600)',
              marginTop: 'var(--spacing-sm)',
            }}
          >
            Acceso verificado. Redirigiendo...
          </p>
        </div>
      </div>
    )
  }

  return null
}
